import { Injectable, Renderer2, RendererFactory2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { THEME_COLORS, ThemeColors } from './theme-colors.config';

export type ThemeName = 'deeppurple-amber' | 'purple-green' | 'azure-blue' | 'black-white';
export type ThemeMode = 'light' | 'dark';

export interface ThemeConfig {
  name: ThemeName;
  displayName: string;
  mode: ThemeMode;
}

const THEME_STORAGE_KEY = 'app-theme';
const MODE_STORAGE_KEY = 'app-theme-mode';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private renderer: Renderer2;
  private currentThemeLink: HTMLLinkElement | null = null;

  // Available themes
  public readonly availableThemes: { name: ThemeName; displayName: string }[] = [
    { name: 'deeppurple-amber', displayName: 'Deep Purple & Amber' },
    { name: 'purple-green', displayName: 'Purple & Green' },
    { name: 'azure-blue', displayName: 'Azure Blue' },
    { name: 'black-white', displayName: 'Black & White' }
  ];

  // Observable streams for theme state
  private currentThemeSubject = new BehaviorSubject<ThemeName>(this.getStoredTheme());
  private currentModeSubject = new BehaviorSubject<ThemeMode>(this.getStoredMode());

  public currentTheme$: Observable<ThemeName> = this.currentThemeSubject.asObservable();
  public currentMode$: Observable<ThemeMode> = this.currentModeSubject.asObservable();

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    // Initialize with stored preferences or defaults
    this.loadTheme(this.currentThemeSubject.value, this.currentModeSubject.value);
  }

  /**
   * Get the currently active theme name
   */
  public getCurrentTheme(): ThemeName {
    return this.currentThemeSubject.value;
  }

  /**
   * Get the currently active theme mode (light/dark)
   */
  public getCurrentMode(): ThemeMode {
    return this.currentModeSubject.value;
  }

  /**
   * Switch to a different theme
   */
  public setTheme(theme: ThemeName): void {
    const mode = this.currentModeSubject.value;
    this.loadTheme(theme, mode);
    this.currentThemeSubject.next(theme);
    this.storeTheme(theme);
  }

  /**
   * Toggle between light and dark mode
   */
  public toggleMode(): void {
    const newMode: ThemeMode = this.currentModeSubject.value === 'light' ? 'dark' : 'light';
    this.setMode(newMode);
  }

  /**
   * Set the theme mode (light or dark)
   */
  public setMode(mode: ThemeMode): void {
    const theme = this.currentThemeSubject.value;
    this.loadTheme(theme, mode);
    this.currentModeSubject.next(mode);
    this.storeMode(mode);
    this.updateBodyClass(mode);
  }

  /**
   * Initialize theme from external configuration (e.g., query parameters)
   * This method is designed for embedded scenarios where the parent site
   * controls the initial theme via query parameters.
   *
   * The query parameters take precedence and will be stored, allowing:
   * 1. Consistent theming when embedded in client websites
   * 2. User preferences to persist across sessions
   * 3. Users can still change themes via the theme switcher
   *
   * @param theme - Optional theme name from query parameter
   * @param mode - Optional theme mode from query parameter
   */
  public initializeFromConfig(theme?: ThemeName, mode?: ThemeMode): void {
    const currentTheme = this.currentThemeSubject.value;
    const currentMode = this.currentModeSubject.value;

    // Validate and use provided parameters, or keep current values
    const finalTheme = theme && this.isValidTheme(theme) ? theme : currentTheme;
    const finalMode = mode === 'light' || mode === 'dark' ? mode : currentMode;

    // Only apply changes if something actually changed
    if (finalTheme !== currentTheme || finalMode !== currentMode) {
      this.loadTheme(finalTheme, finalMode);
      this.currentThemeSubject.next(finalTheme);
      this.currentModeSubject.next(finalMode);

      // Store the configuration for persistence
      // This ensures the embedded app maintains consistent theming
      this.storeTheme(finalTheme);
      this.storeMode(finalMode);
    }
  }

  /**
   * Load the theme CSS file dynamically
   */
  private loadTheme(theme: ThemeName, mode: ThemeMode): void {
    // Remove existing theme link if present
    if (this.currentThemeLink) {
      this.renderer.removeChild(this.document.head, this.currentThemeLink);
    }

    // Create new link element for the theme
    const linkElement = this.renderer.createElement('link') as HTMLLinkElement;
    this.renderer.setAttribute(linkElement, 'rel', 'stylesheet');
    this.renderer.setAttribute(linkElement, 'type', 'text/css');

    // Construct the theme file path - black-white is in root, others in themes folder
    const themePath = theme === 'black-white' ? `/${theme}.css` : `/themes/${theme}.css`;
    this.renderer.setAttribute(linkElement, 'href', themePath);
    this.renderer.setAttribute(linkElement, 'id', 'app-theme');

    // Append to head
    this.renderer.appendChild(this.document.head, linkElement);
    this.currentThemeLink = linkElement;

    // Apply custom CSS variables for the theme
    this.applyThemeColors(theme, mode);

    // Update body class for dark mode
    this.updateBodyClass(mode);
  }

  /**
   * Apply theme-specific CSS custom properties
   */
  private applyThemeColors(theme: ThemeName, mode: ThemeMode): void {
    const themeColors = THEME_COLORS[theme]?.[mode];
    if (!themeColors) {
      return;
    }

    const root = this.document.documentElement;

    // Apply all theme colors as CSS variables
    root.style.setProperty('--primary-color', themeColors.primaryColor);
    root.style.setProperty('--primary-color-rgb', themeColors.primaryColorRgb);
    root.style.setProperty('--primary-color-light', themeColors.primaryColorLight);
    root.style.setProperty('--primary-color-dark', themeColors.primaryColorDark);
    root.style.setProperty('--on-primary-color', themeColors.onPrimaryColor);

    root.style.setProperty('--accent-color', themeColors.accentColor);
    root.style.setProperty('--accent-color-rgb', themeColors.accentColorRgb);
    root.style.setProperty('--on-accent-color', themeColors.onAccentColor);

    root.style.setProperty('--warn-color', themeColors.warnColor);
    root.style.setProperty('--on-warn-color', themeColors.onWarnColor);
    root.style.setProperty('--error-color', themeColors.errorColor);
    root.style.setProperty('--on-error-color', themeColors.onErrorColor);

    root.style.setProperty('--background-color', themeColors.backgroundColor);
    root.style.setProperty('--surface-color', themeColors.surfaceColor);
    root.style.setProperty('--header-background-color', themeColors.headerBackgroundColor);
    root.style.setProperty('--on-background-color', themeColors.onBackgroundColor);
    root.style.setProperty('--on-surface-color', themeColors.onSurfaceColor);
    root.style.setProperty('--on-surface-variant-color', themeColors.onSurfaceVariantColor);
  }

  /**
   * Update body class to support dark mode
   */
  private updateBodyClass(mode: ThemeMode): void {
    const body = this.document.body;
    if (mode === 'dark') {
      this.renderer.addClass(body, 'dark-mode');
    } else {
      this.renderer.removeClass(body, 'dark-mode');
    }
  }

  /**
   * Get stored theme from localStorage
   */
  private getStoredTheme(): ThemeName {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && this.isValidTheme(stored)) {
      return stored as ThemeName;
    }
    return 'deeppurple-amber'; // Default theme
  }

  /**
   * Get stored mode from localStorage
   */
  private getStoredMode(): ThemeMode {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
    return 'light'; // Default mode
  }

  /**
   * Store theme preference
   */
  private storeTheme(theme: ThemeName): void {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }

  /**
   * Store mode preference
   */
  private storeMode(mode: ThemeMode): void {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  }

  /**
   * Validate if a string is a valid theme name
   */
  private isValidTheme(theme: string): boolean {
    return this.availableThemes.some(t => t.name === theme);
  }
}
